import React from 'react';
import { MODULES, COLOR_MAP, COLOR_SELECTED_MAP } from '../../data/modules';

export default function StepSelect({ config, updateConfig, onNext }) {
  const toggle = (id) => {
    const selected = config.selectedModules.includes(id)
      ? config.selectedModules.filter(m => m !== id)
      : [...config.selectedModules, id];
    updateConfig('selectedModules', selected);
  };

  const applyPreset = (preset) => {
    updateConfig('selectedModules', preset.modules);
    if (preset.patch) {
      const patched = preset.patch(config);
      Object.keys(patched).forEach(k => updateConfig(k, patched[k]));
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">เลือกหัวข้อที่ต้องการตั้งค่า</h2>
        <p className="text-gray-500 dark:text-gray-400">เลือกหนึ่งหรือหลายหัวข้อ ระบบจะนำทางคุณผ่านการตั้งค่าแต่ละขั้นตอน</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        {MODULES.map(mod => {
          const isSelected = config.selectedModules.includes(mod.id);
          return (
            <button
              key={mod.id}
              onClick={() => toggle(mod.id)}
              className={`p-4 rounded-xl border-2 text-left transition-all hover:shadow-md ${
                isSelected
                  ? COLOR_SELECTED_MAP[mod.color]
                  : COLOR_MAP[mod.color] + ' hover:border-gray-400 dark:hover:border-gray-500'
              }`}
            >
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0 mt-0.5">{mod.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm">{mod.name}</div>
                  <div className="text-xs opacity-80 mt-0.5">{mod.description}</div>
                  <div className="text-[10px] opacity-60 mt-1">{mod.detail}</div>
                </div>
                <div className="flex-shrink-0 mt-1">
                  {isSelected ? (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-current opacity-40" />
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Quick Presets */}
      <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/50 rounded-xl">
        <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-2">💡 เทมเพลตสำเร็จรูป</h3>
        <p className="text-xs text-amber-700 dark:text-amber-400 mb-3">เลือกเทมเพลตเพื่อตั้งค่าอัตโนมัติ หรือเลือกหัวข้อเองด้านบน</p>
        <div className="flex flex-wrap gap-2">
          {[
            {
              id: 'home',
              name: '🏠 บ้าน',
              modules: ['interface', 'internet', 'timentp', 'firewall', 'security', 'logging'],
              patch: (c) => ({
                hotspot: { ...c.hotspot, enabled: false },
              }),
            },
            {
              id: 'office',
              name: '🏢 สำนักงาน',
              modules: ['interface', 'bridge', 'internet', 'vlans', 'dhcp', 'timentp', 'firewall', 'qos', 'security', 'logging'],
              patch: (c) => ({
                bridges: [
                  {
                    name: 'bridge-lan',
                    comment: 'LAN Bridge',
                    ports: ['ether2', 'ether3', 'ether4', 'ether5'],
                    stp_enabled: true,
                    stp_priority: '0x8000',
                    vlan_filtering: false,
                    pvid: 1,
                    auto_mac: true,
                    mtu: 1500,
                    vlans: [],
                  },
                ],
                vlans: [
                  { id: 10, name: 'vlan-staff', parent_interface: 'bridge-lan', subnet: '192.168.10.1/24', description: 'Staff' },
                  { id: 20, name: 'vlan-guest', parent_interface: 'bridge-lan', subnet: '192.168.20.1/24', description: 'Guest' },
                  { id: 30, name: 'vlan-iot', parent_interface: 'bridge-lan', subnet: '192.168.30.1/24', description: 'IoT' },
                ],
                dhcpServers: [
                  { name: 'dhcp-staff', interface: 'vlan-staff', network: '192.168.10.0/24', pool_start: '192.168.10.100', pool_end: '192.168.10.200', gateway: '192.168.10.1', dns: '192.168.10.1', lease_time: '10m' },
                  { name: 'dhcp-guest', interface: 'vlan-guest', network: '192.168.20.0/24', pool_start: '192.168.20.100', pool_end: '192.168.20.200', gateway: '192.168.20.1', dns: '8.8.8.8', lease_time: '10m' },
                ],
                hotspot: { ...c.hotspot, enabled: false },
              }),
            },
            {
              id: 'school',
              name: '🎓 โรงเรียน',
              modules: ['interface', 'bridge', 'internet', 'timentp', 'hotspot', 'firewall', 'websiteblock', 'security', 'logging'],
              patch: (c) => ({
                bridges: [
                  {
                    name: 'bridge-lan',
                    comment: 'LAN Bridge',
                    ports: ['ether2', 'ether3', 'ether4', 'ether5'],
                    stp_enabled: true,
                    stp_priority: '0x8000',
                    vlan_filtering: false,
                    pvid: 1,
                    auto_mac: true,
                    mtu: 1500,
                    vlans: [],
                  },
                ],
                hotspot: { ...c.hotspot, enabled: true, interface: 'bridge-lan' },
                websiteBlock: { ...c.websiteBlock, enabled: true },
              }),
            },
            {
              id: 'isp',
              name: '🌐 ผู้ให้บริการ',
              modules: ['interface', 'internet', 'loadbalance', 'timentp', 'firewall', 'security', 'logging'],
              patch: (c) => ({
                hotspot: { ...c.hotspot, enabled: false },
              }),
            },
          ].map(preset => (
            <button
              key={preset.id}
              onClick={() => applyPreset(preset)}
              className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-amber-200 dark:border-amber-700 rounded-lg text-xs font-medium text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
            >
              {preset.name}
            </button>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={onNext}
          disabled={config.selectedModules.length === 0}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          ถัดไป
          <span className="badge bg-white/20 text-white">{config.selectedModules.length} หัวข้อ</span>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
        </button>
      </div>
    </div>
  );
}
